<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useLocaleStore } from '~/store/locale';
import { waitForGlobal } from '~/util/script-helpers';
import { PageType, BannerPaypal } from '~/types/paypal';

const props = defineProps({
  value: {
    type: Number || null,
    default: null,
  },
  pageType: {
    type: String as () => PageType,
    default: 'product-details',
  },
  options: {
    type: Object as () => BannerPaypal,
    default: () => ({}),
  },
});

const route = useRoute();
const localeStore = useLocaleStore();
const messageContainer = ref<HTMLElement | null>(null);
const paypal = ref<unknown>(null);
const messageInstance = ref<unknown>(null);

const PAGE_TYPES = [
  { check: (path: string, lang: string) => path === `/${lang}` || path === `/${lang}/`, type: 'home' },
  { check: (path: string) => path.includes('/p/'), type: 'product' },
  { check: (path: string) => path.includes('/plp/'), type: 'category' },
  { check: (path: string) => path.includes('/c/'), type: 'category' },
  { check: (path: string) => path.includes('/cart'), type: 'cart' },
];

const styleLayout = computed(() => {
  return props.options?.layout_type === 'banner' ? 'flex' : props.options?.layout_type;
});

const textPosition = computed(() => {
  return props.options?.layout_type === 'text' && props.options?.text_align ? props.options?.text_align : 'left';
});

const pageTypeVal = computed(() => {
  // return 'home';
  const currentPath = route.path;
  for (const { check, type } of PAGE_TYPES) {
    if (check(currentPath, localeStore.langCode)) return type;
  }
  return 'product';
});

const messageConfig = computed(() => {
  const config = {
    currency: localeStore.currencyCode,
    placement: pageTypeVal.value,
    pageType: props.pageType,
    amount: props.value,
    offer: props.options?.offer,
    style: {},
  };
  if (props.options?.layout_type === 'banner') {
    config.style = {
      ...(styleLayout.value ? { layout: styleLayout.value } : {}),
      ...(props.options?.banner_color ? { color: props.options.banner_color } : {}),
      ...(props.options?.banner_size ? { ratio: props.options.banner_size } : {}),
    };
  }
  if (props.options?.layout_type === 'text') {
    config.style = {
      ...(styleLayout.value ? { layout: styleLayout.value } : {}),
      ...(props.options?.logo_type ? { logo: { type: props.options.logo_type } } : {}),
      ...(props.options?.logo_position ? { logo: { position: props.options.logo_position } } : {}),
      ...(props.options?.text_color ? { text: { color: props.options.text_color } } : {}),
      ...(props.options?.text_size ? { text: { size: props.options.text_size } } : {}),
      ...(props.options?.text_align ? { text: { align: props.options.text_align } } : {}),
    };
  }
  return config;
});

async function renderPayPalMessage() {
  if (!paypal.value || !messageContainer.value) return;

  // Clean up previous instance if needed
  if (messageInstance.value && messageInstance.value?.close) {
    try {
      await messageInstance.value.close();
    } catch {}
    messageInstance.value = null;
    messageContainer.value.innerHTML = '';
  }

  try {
    messageInstance.value = paypal.value.Messages(messageConfig.value);
    await messageInstance.value.render(messageContainer.value);
  } catch (error) {
    console.error('failed to render the PayPal Message', error);
  }
}

onMounted(async () => {
  await waitForGlobal('paypal');
  paypal.value = window.paypal;
  renderPayPalMessage();
});

watch([() => props.value, () => props.pageType], async () => {
  await nextTick();
  renderPayPalMessage();
});
</script>

<template>
  <client-only>
    <div v-if="localeStore?.isUsLocale" :class="['pp-container', `pp-container-${textPosition}`]">
      <div
        ref="messageContainer"
        data-pp-message
        :class="['pp-message', `pp-${textPosition}`]"
        :data-pp-placement="pageTypeVal"
        :data-pp-pageType="pageType"
        :data-pp-amount="value"
        :data-pp-offer="options?.offer"
        :data-pp-style-layout="styleLayout"
        :data-pp-style-logo-type="options?.logo_type"
        :data-pp-style-logo-position="options?.logo_position"
        :data-pp-style-text-color="options?.text_color"
        :data-pp-style-text-size="options?.text_size"
        :data-pp-style-text-align="options?.text_align"
        :data-pp-style-color="options?.banner_color"
        :data-pp-style-ratio="options?.banner_size"
      />
    </div>
  </client-only>
</template>
